"use client";

import { motion } from "framer-motion";
import { Wrench, AlertTriangle, Zap, CheckCircle, Layers } from "lucide-react";
import SectionHero from "../components/SectionHero";
import ContentSection from "../components/ContentSection";
import ServiceCard from "../components/ServiceCard";
import { useEffect } from "react";

const TechnicalServices = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  const services = [
    {
      icon: AlertTriangle,
      title: "Pre-Purchase Assessment",
      description:
        "Comprehensive condition assessments and technical risk identification for vessel acquisitions.",
      features: [
        "Pre-purchase inspections",
        "Maintenance history review",
        "Technical risk identification",
        "Cost estimates for required works",
      ],
    },
    {
      icon: Wrench,
      title: "Dry-Dock Management",
      description:
        "Complete supervision and management of vessel dry-docking and repair operations.",
      features: [
        "Detailed repair list preparation",
        "Yard quote comparison",
        "Work supervision",
        "Cost and timeline monitoring",
      ],
    },
    {
      icon: Layers,
      title: "Newbuilding Support",
      description:
        "Oversight and coordination for newbuilding and major conversion projects.",
      features: [
        "Technical specification review",
        "Shipyard inspections",
        "Build progress monitoring",
        "Quality and timeline management",
      ],
    },
    {
      icon: Zap,
      title: "Ongoing Technical Support",
      description:
        "Daily technical guidance and engineering support for in-service vessels.",
      features: [
        "Defect analysis and solutions",
        "Troubleshooting support",
        "Efficiency improvements",
        "Regulatory updates",
      ],
    },
  ];

  return (
    <div className="pt-20">
      <SectionHero
        title="Technical Services"
        subtitle="Ship Preparation and Ship Management Support"
        description="From first inspection to steady operations, we provide the technical expertise that keeps vessels safe, compliant, and efficient."
      />

      <ContentSection title="Comprehensive Technical Solutions">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {services.map((service, index) => (
            <ServiceCard
              key={index}
              icon={service.icon}
              title={service.title}
              description={service.description}
              features={service.features}
              index={index}
            />
          ))}
        </div>
      </ContentSection>

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="/ship.png"
                alt="Ship Technical Services"
                className="rounded-2xl shadow-2xl"
                height={1000}
                width={1200}
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="mb-6 text-3xl font-bold text-slate-900 sm:text-4xl">
                Proactive Vessel Management
              </h2>
              <p className="mb-6 text-slate-700">
                Our technical services are designed to avoid surprises by
                identifying potential issues early and addressing them before
                they become expensive failures or off-hire events. We combine
                operational experience with strong understanding of regulations
                and commercial pressures.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <CheckCircle className="mt-1 h-6 w-6 flex-shrink-0 text-teal-600" />
                  <div>
                    <h4 className="font-semibold text-slate-900">
                      Planned Maintenance
                    </h4>
                    <p className="text-sm text-slate-600">
                      Preventive maintenance planning to maximize vessel uptime
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <CheckCircle className="mt-1 h-6 w-6 flex-shrink-0 text-teal-600" />
                  <div>
                    <h4 className="font-semibold text-slate-900">
                      Class Compliance
                    </h4>
                    <p className="text-sm text-slate-600">
                      Coordination of surveys and audits for continued
                      compliance
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <CheckCircle className="mt-1 h-6 w-6 flex-shrink-0 text-teal-600" />
                  <div>
                    <h4 className="font-semibold text-slate-900">
                      Safety Systems
                    </h4>
                    <p className="text-sm text-slate-600">
                      Implementation and monitoring of vessel safety management
                      systems
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TechnicalServices;
